/**
 * Instructor shell — Figma "06 · Instructor" top-nav pattern.
 *
 *   Left      : brand dot + "Hauraki AI" wordmark
 *   Middle    : primary nav (Cohorts / Grading / Messages / Students / Notes)
 *   Right     : user chip with dropdown (language + logout)
 *
 * The nav is intentionally sparse — secondary surfaces (Labs, Environments,
 * Announcements, HITL, Grade Appeals) live under the "More" menu so the top
 * strip stays calm. HITL navigation and routes require build opt-in and
 * authenticated runtime availability, never just a frontend flag.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import { useHasUnreadMessages } from '../hooks/useMessages';
import LanguageSwitcher from './LanguageSwitcher';
import { HaurakiMark } from './HaurakiMark';
import { useHitlAvailability } from '../hooks/useHitlAvailability';
import styles from './Layout.module.css';

interface NavItem {
  to: string;
  label: string;
  end?: boolean;
  badgeUnread?: boolean;
}

const PRIMARY_NAV: NavItem[] = [
  { to: '/cohorts', label: 'Cohorts' },
  { to: '/grading', label: 'Grading' },
  { to: '/messages', label: 'Messages', badgeUnread: true },
  { to: '/students', label: 'Students' },
];

const MORE_NAV: NavItem[] = [
  { to: '/overview', label: 'Overview' },
  { to: '/labs', label: 'Labs' },
  { to: '/environments', label: 'Environments' },
  { to: '/announcements', label: 'Announcements' },
  { to: '/hitl', label: 'HITL queue' },
  { to: '/grades/appeals', label: 'Grade appeals' },
  { to: '/at-risk', label: 'At-risk students' },
];

function initialsFrom(nameOrEmail: string | undefined | null): string {
  if (!nameOrEmail) return 'IN';
  const parts = nameOrEmail.trim().split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) return 'IN';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

const Layout: React.FC = () => {
  const oidc = useAuth();
  const navigate = useNavigate();
  const hasUnread = useHasUnreadMessages();
  const hitl = useHitlAvailability();

  const [userOpen, setUserOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const userRef = useRef<HTMLDivElement>(null);
  const moreRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click.
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (userRef.current && !userRef.current.contains(e.target as Node)) {
        setUserOpen(false);
      }
      if (moreRef.current && !moreRef.current.contains(e.target as Node)) {
        setMoreOpen(false);
      }
    };
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') { setUserOpen(false); setMoreOpen(false); } };
    document.addEventListener('mousedown', handler);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', handler); document.removeEventListener('keydown', onKey); };
  }, []);

  const profile = (oidc.user?.profile ?? {}) as Record<string, unknown>;
  const displayName =
    (profile.name as string) ||
    [(profile.given_name as string) || '', (profile.family_name as string) || '']
      .filter(Boolean)
      .join(' ')
      .trim() ||
    (profile.preferred_username as string) ||
    (profile.email as string) ||
    'Instructor';
  const initials = initialsFrom(displayName);

  const handleLogout = () => {
    oidc.signoutRedirect().catch(async () => { await oidc.removeUser(); navigate('/login'); });
  };

  return (
    <div className={styles.container}>
      <a href="#instructor-main" className={styles.skipLink}>Skip to content</a>
      <header className={styles.topNav}>
        <NavLink to="/" className={styles.brand} aria-label="Hauraki AI — home">
          <HaurakiMark size={22} className={styles.brandMark} />
          <span className={styles.brandName}>Hauraki AI</span>
        </NavLink>

        <nav className={styles.nav} aria-label="Primary">
          {PRIMARY_NAV.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `${styles.navItem} ${isActive ? styles.active : ''}`
              }
            >
              {item.label}
              {item.badgeUnread && hasUnread && (
                <span className={styles.unreadDot} aria-label="Unread messages" />
              )}
            </NavLink>
          ))}

          <div className={styles.userWrap} ref={moreRef}>
            <button
              type="button"
              className={styles.navItem}
              onClick={() => setMoreOpen((v) => !v)}
              aria-haspopup="menu"
              aria-expanded={moreOpen}
            >
              More
            </button>
            {moreOpen && (
              <div className={styles.userMenu} role="menu">
                {MORE_NAV.filter((item) => item.to !== '/hitl' || hitl.available).map((item) => (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    end={item.end}
                    onClick={() => setMoreOpen(false)}
                    className={styles.userMenuItem}
                    style={{ display: 'block' }}
                    role="menuitem"
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            )}
          </div>
        </nav>

        <div className={styles.navSpacer} />

        <div className={styles.userWrap} ref={userRef}>
          <button
            type="button"
            className={styles.userChip}
            onClick={() => setUserOpen((v) => !v)}
            aria-haspopup="menu"
            aria-expanded={userOpen}
            aria-label={`Account menu · ${displayName}`}
          >
            <span className={styles.userInitials} aria-hidden="true">
              {initials}
            </span>
            <span className={styles.userMeta}>
              <span className={styles.userName}>{displayName}</span>
              {/* This identifies the surface, not a server-verified account
                  role. OIDC presentation claims may differ from local authority. */}
              <span className={styles.userRole} title="Portal context, not your account role">Instructor portal</span>
            </span>
          </button>
          {userOpen && (
            <div className={styles.userMenu} role="menu">
              <div style={{ padding: '10px 12px' }}>
                <LanguageSwitcher />
              </div>
              <div className={styles.userMenuDivider} />
              <button
                type="button"
                className={styles.userMenuItem}
                onClick={handleLogout}
                role="menuitem"
              >
                Sign out
              </button>
            </div>
          )}
        </div>
      </header>

      <main id="instructor-main" tabIndex={-1} className={styles.main}>
        <Outlet />
      </main>
    </div>
  );
};

export default Layout;
